/**
 * The formula syntax tree, shared by the parser, printer, evaluator, and the
 * reference-rewriting utilities.
 *
 * Conventions fixed here so that every consumer agrees (and so the round-trip
 * properties in Requirement 5 can hold):
 *
 * - `row` / `col` are 0-based, exactly like `A1Ref` in `src/core/a1.ts`.
 * - `call.name` is always upper case. The canonical printer emits upper-case
 *   function names, so normalizing at parse time is what makes
 *   `parse(print(parse(s)))` equal `parse(s)`.
 * - `name.name` (a Named_Range) keeps the case the user typed; the printer
 *   reproduces it verbatim.
 * - On a `range`, the sheet qualifier lives on `from` only. `Sheet1!A1:B2` and
 *   `Sheet1!A1:Sheet1!B2` therefore produce the same tree, and the printer
 *   never has to decide which side to print the sheet on.
 * - Parentheses are not represented. The printer re-inserts the minimum set
 *   required by precedence and associativity.
 */

import type { ErrorCode } from '../types.js';

/** Binary operators, per Requirement 4.2. */
export type BinOp = '+' | '-' | '*' | '/' | '^' | '&' | '=' | '<>' | '<' | '<=' | '>' | '>=';

/** Prefix `+`/`-` and postfix `%`. */
export type UnOp = '+' | '-' | '%';

/** A syntactic cell reference. Shape-compatible with `A1Ref`. */
export interface RefNode {
  k: 'ref';
  sheet?: string;
  absCol: boolean;
  col: number;
  absRow: boolean;
  row: number;
}

/** A syntactic range. The sheet qualifier, if any, lives on `from`. */
export interface RangeNode {
  k: 'range';
  from: RefNode;
  to: RefNode;
}

export type Ast =
  | { k: 'num'; v: number }
  | { k: 'str'; v: string }
  | { k: 'bool'; v: boolean }
  | { k: 'err'; v: ErrorCode }
  | RefNode
  | RangeNode
  | { k: 'name'; name: string }
  | { k: 'call'; name: string; args: Ast[] }
  | { k: 'unary'; op: UnOp; arg: Ast }
  | { k: 'binary'; op: BinOp; left: Ast; right: Ast };

/** Node kind discriminant. */
export type AstKind = Ast['k'];

/**
 * Binding power of each binary operator, lowest binds loosest. Mirrors the
 * EBNF in the design document: comparison < concat < additive < multiplicative
 * < power. Exported so the printer can compute minimal parentheses without
 * importing the parser.
 */
export const BINARY_PRECEDENCE: Readonly<Record<BinOp, number>> = {
  '=': 1,
  '<>': 1,
  '<': 1,
  '<=': 1,
  '>': 1,
  '>=': 1,
  '&': 2,
  '+': 3,
  '-': 3,
  '*': 4,
  '/': 4,
  '^': 5,
};

/** Only `^` is right-associative (`2^3^2` is `2^(3^2)`). */
export function isRightAssociative(op: BinOp): boolean {
  return op === '^';
}

/** Binding power of prefix `+`/`-`: tighter than `^`, looser than postfix `%`. */
export const UNARY_PRECEDENCE = 6;

/** Binding power of postfix `%`. */
export const POSTFIX_PRECEDENCE = 7;

export function isBinOp(op: string): op is BinOp {
  return Object.prototype.hasOwnProperty.call(BINARY_PRECEDENCE, op);
}
